import { Injectable } from '@angular/core';
import { EMPTY, Observable, throwError } from 'rxjs';
import { expand, map, reduce, switchMap, timeout } from 'rxjs/operators';
import { ApiEndpointsService } from 'src/app/core/services/api-endpoints.service';
import { ApiHttpService } from 'src/app/core/services/api-http.service';
import {
  ManagementService,
  DashboardTaskData,
} from '../management/management.service';
import { TeamService } from '../team/team.service';
import {
  AceCalendarEvent,
  AceCalendarEventPage,
  DashboardActivityItem,
  DashboardVehicleContext,
  GpDashboardData,
} from './dashboard-data.models';

const DASHBOARD_API_TIMEOUT_MS = 15000;

export interface DashboardFundResponse {
  dashboard: GpDashboardData;
  fundId: string;
}

@Injectable({ providedIn: 'root' })
export class DashboardDataService {
  constructor(
    private apiHttpService: ApiHttpService,
    private apiEndpointsService: ApiEndpointsService,
    private managementService: ManagementService,
    private teamService: TeamService
  ) {}

  getFundDashboard(
    vehicleId: string,
    gpId: string | undefined,
    vehicleContext: DashboardVehicleContext = {}
  ): Observable<DashboardFundResponse> {
    return this.managementService
      .resolveAceFund(vehicleId, gpId, { allowLegacyNameMatch: false })
      .pipe(
        timeout(DASHBOARD_API_TIMEOUT_MS),
        switchMap(fund => {
          if (!fund) {
            return throwError(() => new Error('FUND_NOT_LINKED'));
          }
          const tenantId =
            fund.tenantId ||
            vehicleContext.tenantId ||
            vehicleContext.gpTenantId ||
            this.readMappedTenantId(vehicleId);
          return this.apiHttpService
            .get(
              this.apiEndpointsService.getGpDashboard({
                fundId: fund.id,
                tenantId,
                priorityLimit: 5,
              })
            )
            .pipe(
              timeout(DASHBOARD_API_TIMEOUT_MS),
              map(response => ({
                dashboard: this.unwrapDashboard(response),
                fundId: fund.id,
              }))
            );
        })
      );
  }

  getFundActivity(fundId: string): Observable<DashboardActivityItem[]> {
    return this.teamService.getFundActivityFeed(fundId).pipe(
      timeout(DASHBOARD_API_TIMEOUT_MS),
      map(activityFeed =>
        (activityFeed.items ?? []).map(item => ({
          id: item.id,
          type: item.type,
          title: item.title,
          subtitle: item.subtitle ?? item.status ?? null,
          status: item.status ?? null,
          occurredAt: item.occurredAt,
        }))
      )
    );
  }

  getCalendarEvents(
    vehicleId: string,
    gpId: string | undefined,
    vehicleContext: DashboardVehicleContext = {}
  ): Observable<AceCalendarEvent[]> {
    return this.managementService
      .resolveAceFund(vehicleId, gpId, { allowLegacyNameMatch: false })
      .pipe(
        timeout(DASHBOARD_API_TIMEOUT_MS),
        switchMap(fund => {
          if (!fund) {
            return throwError(() => new Error('FUND_NOT_LINKED'));
          }
          const tenantId =
            fund.tenantId ||
            vehicleContext.tenantId ||
            vehicleContext.gpTenantId ||
            this.readMappedTenantId(vehicleId);
          const loadPage = (cursor?: string) =>
            this.apiHttpService
              .get(
                this.apiEndpointsService.getAceCalendarEvents({
                  fundId: fund.id,
                  tenantId,
                  limit: 100,
                  cursor,
                  types: ['MEETING', 'EVENT', 'DELIVERY', 'BIRTHDAY'],
                })
              )
              .pipe(
                timeout(DASHBOARD_API_TIMEOUT_MS),
                map(response => this.unwrapCalendarPage(response))
              );

          return loadPage().pipe(
            expand(page =>
              page.nextCursor ? loadPage(page.nextCursor) : EMPTY
            ),
            reduce(
              (events, page) => events.concat(page.items ?? []),
              [] as AceCalendarEvent[]
            )
          );
        })
      );
  }

  getNews(vehicleId: string): Observable<unknown> {
    return this.apiHttpService
      .get(this.apiEndpointsService.getDashboardNewsData(vehicleId))
      .pipe(
        timeout(DASHBOARD_API_TIMEOUT_MS),
        map(response => (response as { data?: unknown })?.data ?? response)
      );
  }

  getTasks(
    vehicleId: string,
    gpId: string | undefined
  ): Observable<DashboardTaskData> {
    return this.managementService.getDashboardTasks(
      vehicleId,
      {
        status: ['TODO', 'IN_PROGRESS', 'BLOCKED', 'IN_REVIEW'],
        includePastDue: true,
      },
      gpId
    ).pipe(timeout(DASHBOARD_API_TIMEOUT_MS));
  }

  getUpdates(vehicleId: string): Observable<unknown> {
    return this.apiHttpService
      .get(this.apiEndpointsService.getDashboardUpdateData(vehicleId))
      .pipe(
        timeout(DASHBOARD_API_TIMEOUT_MS),
        map(response => (response as { data?: unknown })?.data ?? response)
      );
  }

  private unwrapDashboard(response: unknown): GpDashboardData {
    const envelope = response as { data?: GpDashboardData };
    return envelope?.data ?? (response as GpDashboardData);
  }

  private unwrapCalendarPage(response: unknown): AceCalendarEventPage {
    const envelope = response as { data?: AceCalendarEventPage };
    return envelope.data?.items
      ? envelope.data
      : (response as AceCalendarEventPage);
  }

  private readMappedTenantId(vehicleId: string): string | undefined {
    try {
      return (
        localStorage.getItem(`vehicle_tenant_map_${vehicleId}`) ?? undefined
      );
    } catch {
      return undefined;
    }
  }
}
